<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useConfig } from 'dashboard/composables/useConfig';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import PipelinesHeader from './PipelinesHeader.vue';
import PipelineWelcome from './PipelineWelcome.vue';
import Modal from '../../../../components/Modal.vue';
import PipelinesAPI from 'dashboard/api/pipelines';
import PipelineForm from './PipelineForm.vue';
import { emitter } from 'shared/helpers/mitt';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['switch-view']);
const { t } = useI18n();
const store = useStore();
useConfig();
const { isAdmin } = usePipelinePermissions();
const loading = ref(false);
const pipelines = ref([]);
const isLimitReached = ref(false);

// Estado para os modais
const showDeleteModal = ref(false);
const showEditModal = ref(false);
const showWelcomeModal = ref(false);
const showNewPipelineModal = ref(false);
const pipelineToDelete = ref(null);
const pipelineToEdit = ref(null);

// Templates de funis (mesmos do PipelinesHeader)
const pipelineTemplates = [
  {
    id: 'sales',
    name: 'Funil de Vendas',
    icon: 'briefcase',
    description: 'Processo de vendas desde o primeiro contato até o fechamento',
    stages: {
      prospeccao: {
        name: 'Prospecção',
        color: '#6366f1',
        position: 1,
        description: 'Leads e primeiros contatos',
        message_templates: [],
      },
      qualificacao: {
        name: 'Qualificação',
        color: '#8b5cf6',
        position: 2,
        description: 'Análise de fit',
        message_templates: [],
      },
      proposta: {
        name: 'Proposta',
        color: '#ec4899',
        position: 3,
        description: 'Envio e negociação',
        message_templates: [],
      },
      fechamento: {
        name: 'Fechamento',
        color: '#10b981',
        position: 4,
        description: 'Contratos e pagamentos',
        message_templates: [],
      },
    },
  },
  {
    id: 'support',
    name: 'Funil de Suporte',
    icon: 'chat',
    description: 'Atendimento e resolução de tickets de suporte',
    stages: {
      novo: {
        name: 'Novo',
        color: '#6366f1',
        position: 1,
        description: 'Tickets recém abertos',
        message_templates: [],
      },
      analise: {
        name: 'Em Análise',
        color: '#8b5cf6',
        position: 2,
        description: 'Avaliação técnica',
        message_templates: [],
      },
      desenvolvimento: {
        name: 'Em Desenvolvimento',
        color: '#ec4899',
        position: 3,
        description: 'Correção em andamento',
        message_templates: [],
      },
      teste: {
        name: 'Em Teste',
        color: '#f59e0b',
        position: 4,
        description: 'Validação da solução',
        message_templates: [],
      },
      resolvido: {
        name: 'Resolvido',
        color: '#10b981',
        position: 5,
        description: 'Ticket finalizado',
        message_templates: [],
      },
    },
  },
  {
    id: 'recruitment',
    name: 'Funil de Recrutamento',
    icon: 'people',
    description: 'Processo seletivo e contratação',
    stages: {
      triagem: {
        name: 'Triagem',
        color: '#6366f1',
        position: 1,
        description: 'Análise de currículos',
        message_templates: [],
      },
      entrevista: {
        name: 'Entrevista',
        color: '#8b5cf6',
        position: 2,
        description: 'Conversa inicial',
        message_templates: [],
      },
      teste: {
        name: 'Teste Técnico',
        color: '#ec4899',
        position: 3,
        description: 'Avaliação prática',
        message_templates: [],
      },
      proposta: {
        name: 'Proposta',
        color: '#10b981',
        position: 4,
        description: 'Negociação final',
        message_templates: [],
      },
    },
  },
];

const fetchPipelines = async (skipWelcome = false) => {
  loading.value = true;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = data.map(pipeline => {
      // Garantir que os estágios tenham sempre posição definida
      const stages = Object.entries(pipeline.stages || {}).reduce(
        (acc, [id, stage]) => ({
          ...acc,
          [id]: {
            ...stage,
            id,
            position: parseInt(stage.position, 10) || 0,
            message_templates: stage.message_templates || [],
          },
        }),
        {}
      );

      return {
        ...pipeline,
        stages,
      };
    });

    // Limite removido - Enterprise sempre permite pipelines ilimitados
    isLimitReached.value = false;

    // Atualiza o Vuex store para que o PipelineSelector veja as mudanças
    await store.dispatch('pipelines/fetch', { force: true });

    // Mostrar welcome modal se não houver funis (primeira vez)
    if (!skipWelcome && pipelines.value.length === 0 && !loading.value) {
      setTimeout(() => {
        showWelcomeModal.value = true;
      }, 300);
    }
  } catch (error) {
    // Error handled silently
  } finally {
    loading.value = false;
  }
};

// Adiciona o pipeline criado imediatamente à lista local (optimistic update)
// e revalida com fetch em background para garantir consistência.
// Definido após fetchPipelines para evitar no-use-before-define.
const handlePipelineCreated = async pipeline => {
  if (pipeline?.id) {
    const stages = Object.entries(pipeline.stages || {}).reduce(
      (acc, [id, stage]) => ({
        ...acc,
        [id]: {
          ...stage,
          id,
          position: parseInt(stage.position, 10) || 0,
          message_templates: stage.message_templates || [],
        },
      }),
      {}
    );
    const exists = pipelines.value.find(
      p => String(p.id) === String(pipeline.id)
    );
    if (!exists) {
      pipelines.value = [...pipelines.value, { ...pipeline, stages }];
    }
  }
  // Revalida em background para garantir dados completos do servidor
  fetchPipelines(true);
};

const confirmDelete = pipeline => {
  pipelineToDelete.value = pipeline;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  try {
    loading.value = true;
    await store.dispatch('pipelines/deletePipeline', pipelineToDelete.value.id);
    await fetchPipelines();

    emitter.emit('newToastMessage', {
      message: 'Funil excluído com sucesso',
      action: { type: 'success' },
    });

    showDeleteModal.value = false;
    pipelineToDelete.value = null;
  } catch (error) {
    const errorMessage =
      error.response?.data?.error ||
      error.response?.data?.message ||
      t('PIPELINE.ERRORS.DELETE_PIPELINE_WITH_ITEMS');
    emitter.emit('newToastMessage', {
      message: errorMessage,
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const startEdit = pipeline => {
  pipelineToEdit.value = {
    ...pipeline,
    id: String(pipeline.id),
    inbox_id: pipeline.inbox_id || null, // Carrega o inbox_id
    stages: Object.entries(pipeline.stages || {}).reduce(
      (acc, [key, stage]) => ({
        ...acc,
        [String(key)]: {
          ...stage,
          id: String(stage.id || key),
        },
      }),
      {}
    ),
    settings: pipeline.settings || {}, // Preserva todas as configurações
    agents: pipeline.settings?.agents || [], // Carrega os agentes do settings
  };

  showEditModal.value = true;
};

const handleEdit = async () => {
  try {
    loading.value = true;

    // PipelineForm already saved via store action (updatePipeline/createPipeline)
    // Just refresh the local list to stay in sync
    await fetchPipelines();

    emitter.emit('newToastMessage', {
      message: 'Funil atualizado com sucesso',
      action: { type: 'success' },
    });

    showEditModal.value = false;
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao atualizar o funil',
      action: { type: 'error' },
    });
  } finally {
    loading.value = false;
  }
};

const duplicatePipeline = async pipeline => {
  try {
    loading.value = true;
    const timestamp = Date.now();

    // Gera novos IDs para as etapas
    const newStages = Object.entries(pipeline.stages).reduce(
      (acc, [, stage]) => {
        const newId = `stage_${timestamp}_${stage.position}`;
        return {
          ...acc,
          [newId]: {
            ...stage,
            id: newId,
            message_templates: stage.message_templates?.map(template => ({
              ...template,
              id: `template_${timestamp}_${template.id}`,
              stage_id: newId,
            })),
          },
        };
      },
      {}
    );

    const newPipeline = {
      name: `${pipeline.name} (cópia)`,
      description: pipeline.description,
      active: pipeline.active,
      stages: newStages,
      settings: {
        ...pipeline.settings,
        agents: pipeline.settings?.agents || [],
      },
    };

    await store.dispatch('pipelines/createPipeline', newPipeline);
    await fetchPipelines();
  } catch (error) {
    // Error handled silently
  } finally {
    loading.value = false;
  }
};

// Função para ordenar as etapas de um funil por posição
// Filtra etapas de ganho/perdido quando desabilitado no settings
const getSortedStages = (stages, settings = {}) => {
  if (!stages) return [];

  const enableWonLostStages = settings?.enable_won_lost_stages !== false;

  // Converter objeto de etapas em array
  const stagesArray = Object.entries(stages)
    .map(([id, stage]) => ({
      id,
      ...stage,
      position: parseInt(stage.position, 10) || 0,
    }))
    // Filtrar etapas de ganho/perdido quando desabilitado
    .filter(stage => {
      if (enableWonLostStages) return true;
      return !stage.is_won_stage && !stage.is_lost_stage;
    })
    .sort((a, b) => a.position - b.position);

  return stagesArray;
};

const handleWelcomePipelineCreated = async () => {
  showWelcomeModal.value = false;
  await fetchPipelines(true); // Skip welcome modal after creation
};

const handleOpenNewPipelineModal = () => {
  showNewPipelineModal.value = true;
};

onMounted(() => {
  fetchPipelines();
  // Listen for event to open new pipeline modal from scratch
  emitter.on('open-new-pipeline-modal', handleOpenNewPipelineModal);
});

onUnmounted(() => {
  emitter.off('open-new-pipeline-modal', handleOpenNewPipelineModal);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="flex flex-col h-full bg-n-background">
    <PipelinesHeader
      @switch-view="view => emit('switch-view', view)"
      @pipeline-created="handlePipelineCreated"
    />

    <div class="flex-1 overflow-y-auto min-h-0">
      <!-- Alerta de Limite -->
      <div
        v-if="isLimitReached"
        class="mx-6 mt-4 px-4 py-2.5 bg-n-amber-3 border border-n-amber-9 rounded-lg"
      >
        <div class="flex items-center gap-2">
          <fluent-icon
            icon="warning"
            size="14"
            class="text-n-amber-11 flex-shrink-0"
          />
          <p class="text-xs text-n-slate-12">
            <span class="font-medium">Limite atingido:</span> Máximo de 2
            pipelines. Atualize para PRO.
          </p>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-20">
        <div class="flex flex-col items-center gap-3">
          <span
            class="w-8 h-8 border-2 border-n-weak border-t-n-brand rounded-full animate-spin"
          />
          <p class="text-sm text-n-slate-11">Carregando pipelines...</p>
        </div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!pipelines.length"
        class="flex flex-col items-center justify-center py-20"
      >
        <div
          class="w-16 h-16 rounded-2xl bg-n-alpha-2 flex items-center justify-center mb-4"
        >
          <fluent-icon icon="briefcase" size="28" class="text-n-slate-10" />
        </div>
        <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
          Nenhum pipeline encontrado
        </h3>
        <p class="text-xs text-n-slate-11 text-center max-w-sm">
          Crie seu primeiro pipeline para organizar seus leads e negócios
        </p>
      </div>

      <!-- Pipelines List -->
      <div v-else class="p-6 space-y-3">
        <div
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          class="group bg-n-solid-2 rounded-xl border border-n-weak hover:border-n-brand/20 hover:shadow-lg hover:shadow-n-brand/5 transition-all duration-300"
        >
          <!-- Card Content -->
          <div class="p-5">
            <!-- Top Row: Name + Status + Actions -->
            <div class="flex items-start justify-between mb-4">
              <div class="flex items-center gap-3 min-w-0">
                <!-- Pipeline Icon -->
                <div
                  class="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  :class="
                    pipeline.active
                      ? 'bg-n-brand/10 text-n-brand'
                      : 'bg-n-alpha-2 text-n-slate-10'
                  "
                >
                  <fluent-icon icon="briefcase" size="18" />
                </div>
                <div class="min-w-0">
                  <div class="flex items-center gap-2">
                    <h3
                      class="text-sm font-semibold text-n-slate-12 truncate leading-tight"
                    >
                      {{ pipeline.name }}
                    </h3>
                    <span
                      class="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md font-medium flex-shrink-0"
                      :class="
                        pipeline.active
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-alpha-2 text-n-slate-10'
                      "
                    >
                      <span
                        class="w-1 h-1 rounded-full"
                        :class="
                          pipeline.active ? 'bg-n-teal-9' : 'bg-n-slate-8'
                        "
                      />
                      {{ pipeline.active ? 'Ativo' : 'Inativo' }}
                    </span>
                  </div>
                  <div class="flex items-center gap-3 mt-0.5">
                    <span class="text-[11px] text-n-slate-10">
                      {{
                        new Date(pipeline.created_at).toLocaleDateString(
                          'pt-BR',
                          { day: '2-digit', month: 'short', year: 'numeric' }
                        )
                      }}
                    </span>
                    <span
                      class="text-[11px] text-n-slate-10 flex items-center gap-1"
                    >
                      <fluent-icon icon="book" size="10" />
                      {{
                        getSortedStages(pipeline.stages, pipeline.settings)
                          .length
                      }}
                      etapas
                    </span>
                    <span
                      v-if="pipeline.settings?.agents?.length"
                      class="text-[11px] text-n-slate-10 flex items-center gap-1"
                    >
                      <fluent-icon icon="people" size="10" />
                      {{ pipeline.settings.agents.length }} agentes
                    </span>
                  </div>
                </div>
              </div>

              <!-- Actions -->
              <div
                v-if="isAdmin"
                class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              >
                <button
                  class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
                  title="Editar"
                  @click="startEdit(pipeline)"
                >
                  <fluent-icon icon="edit" size="14" />
                </button>
                <button
                  class="p-1.5 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1 transition-colors"
                  title="Duplicar"
                  @click="duplicatePipeline(pipeline)"
                >
                  <fluent-icon icon="copy" size="14" />
                </button>
                <button
                  class="p-1.5 rounded-md text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-3 transition-colors"
                  title="Excluir"
                  @click="confirmDelete(pipeline)"
                >
                  <fluent-icon icon="delete" size="14" />
                </button>
              </div>
            </div>

            <!-- Stage Flow -->
            <div class="flex items-center gap-1 overflow-x-auto pb-1">
              <template
                v-for="(stage, index) in getSortedStages(
                  pipeline.stages,
                  pipeline.settings
                )"
                :key="stage.id"
              >
                <!-- Connector Arrow -->
                <div v-if="index > 0" class="flex-shrink-0 text-n-slate-8">
                  <fluent-icon icon="chevron-right" size="10" />
                </div>
                <!-- Stage Chip -->
                <div
                  class="group/stage relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg flex-shrink-0 border transition-colors duration-150"
                  :style="{
                    borderColor: `${stage.color}25`,
                    backgroundColor: `${stage.color}08`,
                  }"
                >
                  <div
                    class="w-2 h-2 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: stage.color }"
                  />
                  <span
                    class="text-[11px] font-medium text-n-slate-12 whitespace-nowrap"
                  >
                    {{ stage.name }}
                  </span>
                  <span
                    v-if="stage.message_templates?.length"
                    class="text-[9px] px-1 py-px rounded bg-n-alpha-2 text-n-slate-11 font-medium"
                  >
                    {{ stage.message_templates.length }}
                  </span>

                  <!-- Tooltip -->
                  <div
                    v-if="stage.message_templates?.length"
                    class="absolute hidden group-hover/stage:block bg-n-solid-3 shadow-xl rounded-lg p-3 z-50 min-w-[180px] top-full left-0 mt-2 border border-n-weak"
                  >
                    <p
                      class="text-[10px] font-semibold text-n-slate-10 mb-1.5 uppercase tracking-wider"
                    >
                      Templates
                    </p>
                    <div class="space-y-1">
                      <div
                        v-for="template in stage.message_templates"
                        :key="template.id"
                        class="text-[11px] text-n-slate-12 truncate flex items-center gap-1.5"
                      >
                        <div class="w-1 h-1 rounded-full bg-n-slate-9" />
                        {{ template.title }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- Agents Row -->
            <div
              v-if="pipeline.settings?.agents?.length"
              class="flex items-center gap-2 mt-3 pt-3 border-t border-n-weak/50"
            >
              <div
                class="flex items-center -space-x-1.5 hover:space-x-0.5 transition-all"
              >
                <div
                  v-for="agent in pipeline.settings.agents"
                  :key="agent.id"
                  class="transition-all"
                  :title="agent.name"
                >
                  <Avatar
                    :name="agent.name || ''"
                    :src="agent.thumbnail || ''"
                    :size="20"
                    class="ring-2 ring-n-solid-2"
                  />
                </div>
              </div>
              <span class="text-[10px] text-n-slate-10">
                {{
                  pipeline.settings.agents
                    .map(a => a.name?.split(' ')[0])
                    .join(', ')
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Edição -->
    <Modal
      v-model:show="showEditModal"
      class="pipeline-modal"
      @close="() => (showEditModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.PIPELINES.EDIT.TITLE') }}
        </h3>
        <PipelineForm
          v-if="pipelineToEdit"
          is-editing
          :initial-data="pipelineToEdit"
          @saved="handleEdit"
          @close="showEditModal = false"
        />
      </div>
    </Modal>

    <!-- Modal de Confirmação de Exclusão -->
    <Modal
      v-model:show="showDeleteModal"
      @close="() => (showDeleteModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.PIPELINES.DELETE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mb-6">
          {{
            t('PIPELINE.PIPELINES.DELETE.DESCRIPTION', {
              name: pipelineToDelete?.name,
            })
          }}
        </p>
        <div class="flex justify-end gap-2">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="showDeleteModal = false"
          >
            {{ t('PIPELINE.CANCEL') }}
          </Button>
          <Button
            variant="solid"
            color="ruby"
            size="sm"
            :is-loading="loading"
            @click="handleDelete"
          >
            {{ t('PIPELINE.DELETE') }}
          </Button>
        </div>
      </div>
    </Modal>

    <!-- Welcome Modal for First-Time Users -->
    <PipelineWelcome
      :show="showWelcomeModal"
      :templates="pipelineTemplates"
      @close="showWelcomeModal = false"
      @pipeline-created="handleWelcomePipelineCreated"
    />

    <!-- Modal de Novo Funil (from scratch) -->
    <Modal
      v-model:show="showNewPipelineModal"
      class="pipeline-modal"
      @close="() => (showNewPipelineModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
        </h3>
        <PipelineForm
          :initial-data="{
            name: '',
            description: '',
            stages: {},
            settings: { agents: [] },
          }"
          @saved="
            async pipeline => {
              showNewPipelineModal = false;
              await fetchPipelines(true);
            }
          "
          @close="showNewPipelineModal = false"
        />
      </div>
    </Modal>
  </div>
</template>

<style scoped>
/* Modal deep styling */
:deep(.pipeline-modal .modal-container) {
  max-width: 1400px;
  width: 95vw;
}
</style>
